import React, { useState } from 'react'

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
    <path d="M1 1L17 17M17 1L1 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const SuccessModal = ({ onClose }) => (
  <div className="td-success-overlay">
    <div className="td-success-modal" onClick={e => e.stopPropagation()}>
      <div className="td-success-icon-wrapper">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12L11 15L16 9" />
        </svg>
      </div>
      <h3 className="td-success-title">Profile Updated</h3>
      <p className="td-success-message">Your details have been saved successfully.</p>
      <div className="td-success-footer">
        <button className="td-success-btn" onClick={onClose}>Got it</button>
      </div>
    </div>
  </div>
)

const ProfileDrawer = ({ onClose, onProfileUpdate, tenantData: initialTenantData }) => {
  const [form, setForm] = useState({
    name:              initialTenantData?.TenantName     || '',
    phone:             initialTenantData?.Mobile         || '',
    email:             initialTenantData?.Email          || '',
    permanentAddress:  initialTenantData?.Address        || '',
    occupation:        initialTenantData?.Company        || '',
    emergencyName:     initialTenantData?.GuardianName   || '',
    emergencyPhone:    initialTenantData?.GuardianMobile || '',
    emergencyRelation: initialTenantData?.Relation       || 'Parent',
  })

  const [errors, setErrors]     = useState({})
  const [loading, setLoading]   = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const validators = {
    name:           v => v.trim() ? '' : 'Name is required',
    phone:          v => /^\d{10}$/.test(v.trim()) ? '' : 'Enter a valid 10-digit number',
    email:          v => !v.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? '' : 'Enter a valid email',
    emergencyName:  v => v.trim() ? '' : 'Contact name is required',
    emergencyPhone: v => /^\d{10}$/.test(v.trim()) ? '' : 'Enter a valid 10-digit number',
  }

  const validate = (field, value) => {
    const err = validators[field] ? validators[field](value) : ''
    setErrors(prev => ({ ...prev, [field]: err }))
    return !err
  }

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) validate(field, value)
  }

  const handleBlur = (field) => validate(field, form[field])

  const handleSubmit = async () => {
    const fields = ['name', 'phone', 'email', 'emergencyName', 'emergencyPhone']
    const valid = fields.map(f => validate(f, form[f]))
    if (valid.includes(false)) return

    setLoading(true)
    try {
      // Prepare data for API update
      const updatedData = {
        TenantID: initialTenantData?.TenantID,
        TenantName: form.name,
        Mobile: form.phone,
        Email: form.email,
        Address: form.permanentAddress,
        Company: form.occupation,
        GuardianName: form.emergencyName,
        GuardianMobile: form.emergencyPhone,
        Relation: form.emergencyRelation,
      }
      
      // Call your actual API here
      // await updateTenantProfileAPI(updatedData)
      
      // Simulate API call
      await new Promise(r => setTimeout(r, 700))
      
      // Update the context/callback
      if (onProfileUpdate) {
        onProfileUpdate(updatedData)
      }
      
      setShowSuccess(true)
    } catch (err) {
      setErrors(prev => ({ ...prev, api: err.message || 'Something went wrong. Please try again.' }))
    } finally {
      setLoading(false)
    }
  }

  const initials = form.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)

  return (
    <>
      <div className="td-profile-overlay" onClick={onClose} />

      <div className="td-profile-drawer td-profile-drawer--open">

        <div className="td-profile-head">
          <div className="td-profile-avatar-lg">{initials}</div>
          <div className="td-profile-head-info">
            <span className="td-profile-head-name">{form.name}</span>
            <span className="td-profile-head-sub">{form.phone}</span>
          </div>
          <button className="td-profile-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="td-profile-body">

          <div className="td-profile-section">
            <div className="td-profile-section-head">
              <span className="td-profile-section-title">Personal Information</span>
            </div>
            <div className="td-form-grid">

              <div className="td-field td-field-full">
                <label className="td-label">Full Name <span className="td-req">*</span></label>
                <input
                  className={`td-input ${errors.name ? 'td-input-err' : ''}`}
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={e => handleChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                />
                {errors.name && <span className="td-err">{errors.name}</span>}
              </div>

              <div className="td-field">
                <label className="td-label">Phone <span className="td-req">*</span></label>
                <input
                  className={`td-input ${errors.phone ? 'td-input-err' : ''}`}
                  type="tel"
                  maxLength={10}
                  placeholder="10-digit number"
                  value={form.phone}
                  onChange={e => handleChange('phone', e.target.value.replace(/\D/g, ''))}
                  onBlur={() => handleBlur('phone')}
                />
                {errors.phone && <span className="td-err">{errors.phone}</span>}
              </div>

              <div className="td-field">
                <label className="td-label">Email</label>
                <input
                  className={`td-input ${errors.email ? 'td-input-err' : ''}`}
                  type="email"
                  placeholder="tenant@email.com"
                  value={form.email}
                  onChange={e => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                />
                {errors.email && <span className="td-err">{errors.email}</span>}
              </div>

              <div className="td-field td-field-full">
                <label className="td-label">Permanent Address</label>
                <textarea
                  className="td-textarea"
                  rows={2}
                  placeholder="House no., Street, City, State - PIN"
                  value={form.permanentAddress}
                  onChange={e => handleChange('permanentAddress', e.target.value)}
                />
              </div>

              <div className="td-field td-field-full">
                <label className="td-label">Occupation</label>
                <input
                  className="td-input"
                  type="text"
                  placeholder="e.g. Software Engineer"
                  value={form.occupation}
                  onChange={e => handleChange('occupation', e.target.value)}
                />
              </div>

            </div>
          </div>

          <div className="td-profile-section">
            <div className="td-profile-section-head">
              <span className="td-profile-section-title">Emergency Contact</span>
            </div>
            <div className="td-form-grid">

              <div className="td-field td-field-full">
                <label className="td-label">Contact Name <span className="td-req">*</span></label>
                <input
                  className={`td-input ${errors.emergencyName ? 'td-input-err' : ''}`}
                  type="text"
                  placeholder="Full name"
                  value={form.emergencyName}
                  onChange={e => handleChange('emergencyName', e.target.value)}
                  onBlur={() => handleBlur('emergencyName')}
                />
                {errors.emergencyName && <span className="td-err">{errors.emergencyName}</span>}
              </div>

              <div className="td-field">
                <label className="td-label">Phone <span className="td-req">*</span></label>
                <input
                  className={`td-input ${errors.emergencyPhone ? 'td-input-err' : ''}`}
                  type="tel"
                  maxLength={10}
                  placeholder="10-digit number"
                  value={form.emergencyPhone}
                  onChange={e => handleChange('emergencyPhone', e.target.value.replace(/\D/g, ''))}
                  onBlur={() => handleBlur('emergencyPhone')}
                />
                {errors.emergencyPhone && <span className="td-err">{errors.emergencyPhone}</span>}
              </div>

              <div className="td-field">
                <label className="td-label">Relationship</label>
                <select
                  className="td-select"
                  value={form.emergencyRelation}
                  onChange={e => handleChange('emergencyRelation', e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="Parent">Parent</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </select>
              </div>

            </div>
          </div>

          {errors.api && (
            <div className="td-profile-section">
              <span className="td-err">{errors.api}</span>
            </div>
          )}

        </div>

        <div className="td-profile-footer">
          <button className="td-btn td-btn-outline" onClick={onClose}>Cancel</button>
          <button className="td-btn td-btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

      </div>

      {showSuccess && (
        <SuccessModal onClose={() => { 
          setShowSuccess(false)
          onClose() 
        }} />
      )}
    </>
  )
}

export default ProfileDrawer